import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

import { XoneraSpinner } from '@/components/core/common/feedback/XoneraSpinner';
import { AuthCard, AuthLayout } from '@/components/features/auth/AuthLayout';
import {
  AuthStatusButton,
  AuthStatusCard,
  AuthStatusLink,
} from '@/components/features/auth/status';
import { track } from '@/config/observability';
import { queryClient, queryKeys } from '@/config/query-client';
import { supabase } from '@/config/supabase';
import type { User } from '@supabase/supabase-js';
import { ApiError, userApi } from '@/lib/api-client';

/**
 * Error params off the return URL. Supabase reports a failed redirect in the
 * query string or, under the implicit flow, in the `#` fragment - which is
 * where a cancelled Google consent screen lands - so both are read.
 */
function readRedirectError(searchParams: URLSearchParams) {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const get = (key: string) => searchParams.get(key) ?? hash.get(key);
  const error = get('error');
  const errorCode = get('error_code');
  if (!error && !errorCode) return null;
  return {
    // Backing out of Google's consent screen comes back as a bare
    // `access_denied`. An expired email link ALSO says `access_denied`, but
    // with an `error_code` (`otp_expired`) - so the code is what tells them
    // apart, and the expired link keeps its card.
    cancelled: error === 'access_denied' && !errorCode,
    description: get('error_description'),
  };
}

/**
 * Load the profile before entering the app - which is also the request that
 * creates the account on a first sign-in. Fail-open like `OnboardingGate`: any
 * error except the 409 lets the app handle it. The 409 is the backend refusing
 * a second sign-in identity for an email another account already holds; the
 * app could do nothing with that session, so it is dropped here instead.
 */
async function emailClash(): Promise<string | null> {
  try {
    await queryClient.fetchQuery({
      queryKey: queryKeys.user.profile(),
      queryFn: () => userApi.getProfile(),
      // The client's default retries would hold a 409 - which no retry will
      // change - behind seconds of backoff on the "Signing you in" card.
      retry: false,
    });
    return null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      await supabase.auth.signOut({ scope: 'local' });
      return err.detail;
    }
    return null;
  }
}

/** The terminal error card, with the two ways out of a dead link. */
function CallbackFailure({ title, body }: { title: string; body: string }) {
  const navigate = useNavigate();
  return (
    <AuthStatusCard
      tone='error'
      icon={<AlertTriangle className='h-5 w-5' />}
      title={title}
      body={body}
    >
      <AuthStatusButton
        onClick={() => navigate('/auth?mode=login', { replace: true })}
      >
        Back to sign in
      </AuthStatusButton>
      <AuthStatusLink
        onClick={() => navigate('/auth?mode=reset', { replace: true })}
      >
        Send me a new link
      </AuthStatusLink>
    </AuthStatusCard>
  );
}

/**
 * `/auth/callback` - where every emailed link lands (sign-up confirmation,
 * magic link) and where Google sign-in returns.
 *
 * Supabase puts a one-time `?code` on the URL, which is exchanged for a
 * session. On success the user is already signed in, so there is nothing to
 * show and this navigates straight to the app - the old "Success!" card was a
 * screen nobody needed to read.
 *
 * The failure branch used to render raw gradients (`from-red-50 to-rose-100`,
 * `from-green-50 to-emerald-100`) with no dark variants at all, which made it
 * the last auth surface with a broken dark mode. It is now the same
 * `AuthStatusCard` every other terminal auth state uses.
 */
const RECENT_MS = 10 * 60 * 1000;
const isRecent = (iso: string | undefined) =>
  !!iso && Date.now() - new Date(iso).getTime() < RECENT_MS;

/**
 * This callback serves Google sign-in, sign-up confirmation and magic links.
 * Google: an account created in the last few minutes is a sign-up. Email: a
 * confirmation is the request that set `email_confirmed_at`, so a recent one
 * means this link was the confirmation; otherwise it was a magic link.
 */
function trackCallbackSignIn(user: User) {
  if (user.app_metadata?.provider === 'google') {
    if (isRecent(user.created_at)) {
      track('signed_up', { method: 'google', needs_confirmation: false });
    } else {
      track('logged_in', { method: 'google' });
    }
    return;
  }
  if (isRecent(user.email_confirmed_at)) track('email_confirmed');
  else track('logged_in', { method: 'magic_link' });
}

/** A Google return is a fresh sign-in; an email link opened twice is not. */
const googleOnly = (user: User | undefined) =>
  user?.app_metadata?.provider === 'google' ? user : undefined;

export function AuthCallbackPage() {
  const [error, setError] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState('That link has expired');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // `track` names who signed in, once the account is known to be usable.
    const enter = async (signedIn?: User | null) => {
      const clash = await emailClash();
      if (clash) {
        setErrorTitle('This email already has an account');
        setError(clash);
      } else {
        if (signedIn) trackCallbackSignIn(signedIn);
        navigate('/', { replace: true });
      }
    };

    const handleAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const redirectError = readRedirectError(searchParams);

        if (redirectError) {
          // Cancelling at Google is a choice, not a failure: straight back
          // to sign-in, no error card to dismiss.
          if (redirectError.cancelled) {
            navigate('/auth?mode=login', { replace: true });
            return;
          }
          setError(
            redirectError.description ||
              'This link is invalid or has already been used.'
          );
          return;
        }

        if (code) {
          const { data, error: exchangeError } =
            await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) setError(exchangeError.message);
          else if (data.session) await enter(data.session.user);
          return;
        }

        // No code on the URL. Either the session is already live (the link was
        // opened twice, or a Google sign-in came back with its tokens in the
        // fragment) or there is nothing to act on.
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session) await enter(googleOnly(session.user));
        else navigate('/auth?mode=login', { replace: true });
      } catch {
        setError('Something went wrong while confirming your link.');
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  return (
    <AuthLayout>
      <AuthCard>
        {error ? (
          <CallbackFailure title={errorTitle} body={error} />
        ) : (
          <AuthStatusCard
            tone='info'
            icon={<XoneraSpinner className='h-5 w-5' label='' />}
            title='Signing you in'
            body='One moment while we confirm your link.'
          />
        )}
      </AuthCard>
    </AuthLayout>
  );
}
